<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ScheduledMessageCard from './ScheduledMessageCard.vue';
import EditScheduledMessageModal from 'dashboard/routes/dashboard/scheduledMessages/EditScheduledMessageModal.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  scheduledMessages: { type: Array, default: () => [] },
  conversationId: { type: Number, required: true },
});

const store = useStore();
const { t } = useI18n();

const showEditModal = ref(false);
const selectedMessage = ref(null);
const sendingMessageId = ref(null);
const cancellingMessageId = ref(null);

const hasScheduledMessages = computed(() => props.scheduledMessages.length > 0);

const onEdit = message => {
  selectedMessage.value = message;
  showEditModal.value = true;
};

const onCancel = async message => {
  if (cancellingMessageId.value) return;
  cancellingMessageId.value = message.id;
  try {
    await store.dispatch('scheduledMessages/cancel', message.id);
    store.commit('REMOVE_SCHEDULED_MESSAGE_FROM_CONVERSATION', {
      conversationId: props.conversationId,
      scheduledMessageId: message.id,
    });
    useAlert(t('SCHEDULED_MESSAGES.API.CANCEL_SUCCESS'));
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.API.ERROR_MESSAGE'));
  } finally {
    cancellingMessageId.value = null;
  }
};

const onSendNow = async message => {
  if (sendingMessageId.value) return;
  sendingMessageId.value = message.id;
  try {
    await store.dispatch('scheduledMessages/sendNow', message.id);
    store.commit('REMOVE_SCHEDULED_MESSAGE_FROM_CONVERSATION', {
      conversationId: props.conversationId,
      scheduledMessageId: message.id,
    });
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGE.SEND_NOW_SUCCESS'));
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.API.ERROR_MESSAGE'));
  } finally {
    sendingMessageId.value = null;
  }
};

const onModalClose = () => {
  showEditModal.value = false;
  selectedMessage.value = null;
};

const onModalUpdated = updatedMessage => {
  if (updatedMessage) {
    store.commit('UPDATE_SCHEDULED_MESSAGE_IN_CONVERSATION', {
      conversationId: props.conversationId,
      scheduledMessage: updatedMessage,
    });
  }
  showEditModal.value = false;
  selectedMessage.value = null;
};
</script>

<template>
  <li v-if="hasScheduledMessages" class="list-none">
    <!-- Divider -->
    <div class="flex items-center gap-2 my-2 mx-4">
      <div class="flex-1 h-px bg-n-amber-6" />
      <span class="text-xs font-medium text-n-amber-11 flex items-center gap-1">
        <Icon icon="i-lucide-calendar-clock" class="size-3" />
        {{ $t('CONVERSATION.SCHEDULED_MESSAGE.SECTION_TITLE') }}
      </span>
      <div class="flex-1 h-px bg-n-amber-6" />
    </div>

    <!-- Cards -->
    <TransitionGroup
      tag="div"
      class="flex flex-col gap-2 mx-4"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-300 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-x-8"
      move-class="transition-transform duration-300 ease-out"
    >
      <ScheduledMessageCard
        v-for="message in scheduledMessages"
        :key="message.id"
        :scheduled-message="message"
        :is-sending="sendingMessageId === message.id"
        :is-cancelling="cancellingMessageId === message.id"
        @edit="onEdit"
        @cancel="onCancel"
        @send-now="onSendNow"
      />
    </TransitionGroup>

    <!-- Edit Modal -->
    <EditScheduledMessageModal
      v-model:show="showEditModal"
      :scheduled-message="selectedMessage"
      @close="onModalClose"
      @updated="onModalUpdated"
    />
  </li>
</template>
